<template>
  <div class="export-summary-export-panel">
    <BittsCallout
      v-if="showMatchingAlgoBanner"
      title="We're improving how matching works"
      subtitle="Your export numbers may change as new fields and data points are added for greater accuracy."
      size="x-small"
      :learn-more-info="{
        url: 'https://help.crossbeam.com/en/articles/3160297-crossbeam-matching-enginethis',
      }"
      type="info"
      class="mb-16"
    />
    <BittsCallout
      v-if="showCallout"
      :title="NEW_RECORD_EXPORT_CALLOUTS.buildTitle(limitUsedPercent)"
      :subtitle="calloutSubtitle"
      action-text="Contact us"
      size="x-small"
      :type="exportLimitStatus === 'limit-reached' ? 'danger' : 'warning'"
      :action-button-type="
        exportLimitStatus === 'limit-reached' ? 'danger' : 'neutral'
      "
      class="mb-16"
      @bitts-callout-action="onAlertClicked"
    />
    <RecordExportSummary />
    <SummaryPanelRow
      v-for="{
        destination,
        title,
        totalCount,
        uniqueCount,
        to,
      } in destinationsWithCountsAndRoutes"
      :key="destination"
      :title
      :to
      :denominator="totalCount"
      :numerator="uniqueCount"
      :color="color"
      @clicked:destination="emit('clicked:destination')"
    >
      <template #row-avatar>
        <BittsAvatar
          v-if="showAvatar(destination)"
          size="x-small"
          :is-entity="true"
          :org="buildAvatar(destination)"
          class="mr-8"
          data-testid="bucket-avatar"
        />
        <FontAwesomeIcon
          v-else
          :icon="buildIcon(destination)"
          class="mr-8 text-16 text-neutral-accent"
        />
      </template>
    </SummaryPanelRow>
    <p v-if="destinationsWithCounts.length > 0">
      Unique records are counted when the record leaves Crossbeam for the first
      time
    </p>
  </div>
</template>

<script setup lang="ts">
import { BittsAvatar, BittsCallout } from '@crossbeam/bitts';
import { StrictExclude } from '@crossbeam/types';

import { computed } from 'vue';

import useBilling from '@/composables/useBilling';
import useRecordExportLimits, {
  Destination,
} from '@/composables/useRecordExportLimits';
import { NEW_RECORD_EXPORT_CALLOUTS } from '@/constants/record_exports';
import { useFlashesStore } from '@/stores';
import { TrayTag } from '@/types/integrations';

import RecordExportSummary from './RecordExportSummary.vue';
import SummaryPanelRow from './SummaryPanelRow.vue';

const emit = defineEmits<{
  'clicked:destination': [];
}>();

const showBannerDate = new Date('2025-11-03T00:00:00Z');
const today = new Date();
const showMatchingAlgoBanner = today >= showBannerDate;

const { exportLimitStatus, limitUsedPercent, destinationsWithCounts } =
  useRecordExportLimits();

const destinationsWithCountsAndRoutes = computed(() =>
  destinationsWithCounts.value.map((destination) => ({
    ...destination,
    to: buildRoute(destination.destination),
  })),
);

const alertInfo = computed(() => {
  if (!exportLimitStatus.value || exportLimitStatus.value === 'over-75')
    return null;
  return NEW_RECORD_EXPORT_CALLOUTS[exportLimitStatus.value];
});

const showCallout = computed(() => !!alertInfo.value);
const calloutSubtitle = computed(() => alertInfo.value?.message || '');

const color = computed(() => {
  switch (exportLimitStatus.value) {
    case 'limit-reached':
      return 'danger';
    case 'over-90':
      return 'warning';
    default:
      return 'secondary';
  }
});

const mapBucketToAvatar: Record<
  StrictExclude<
    Destination,
    | 'rest_api'
    | 'report_exports'
    | 'webhook'
    | 'pipeline_generation_export'
    | 'deal_navigator_export'
  >,
  string
> = {
  salesforce_custom_object: 'Salesforce',
  salesforce_standard_object: 'Salesforce',
  hubspot_custom_object: 'Hubspot',
  hubspot_standard_object: 'Hubspot',
  snowflake_custom_object: 'Snowflake',
  databricks_custom_object: 'Databricks',
  microsoft_dynamics_custom_object: 'MSDynamics',
  bigquery_custom_object: 'BigQuery',
  leandata: 'LeanData',
  partnerportal: 'PartnerPortal',
  clari_custom_object: 'Clari',
  marketo: 'AdobeMarketo',
  superglue: 'Superglue',
  clay: 'Clay',
  fivetran: 'Fivetran',
};

const buildTrayRoute = (trayTag: TrayTag) => `/integrations/${trayTag}/manage`;

function buildRoute(destination: Destination) {
  const trayTagMap: Partial<Record<Destination, TrayTag>> = {
    salesforce_custom_object: 'xb-salesforce-overlaps-push',
    salesforce_standard_object: 'xb-salesforce-overlaps-push',
    hubspot_custom_object: 'xb-hubspot',
    hubspot_standard_object: 'xb-hubspot',
    microsoft_dynamics_custom_object: 'xb-microsoft-dynamics',
    clari_custom_object: 'xb-clari',
    marketo: 'xb-marketo',
  };

  return trayTagMap[destination] ? buildTrayRoute(trayTagMap[destination]) : '';
}

const buildAvatar = (destination: Destination) => {
  return destination in mapBucketToAvatar
    ? {
        logo_url: new URL(
          `../../assets/pngs/partner-cloud/${mapBucketToAvatar[destination as keyof typeof mapBucketToAvatar]}.png`,
          import.meta.url,
        ).href,
      }
    : undefined;
};

function showAvatar(destination: Destination) {
  return ![
    'report_exports',
    'rest_api',
    'webhook',
    'pipeline_generation_export',
    'deal_navigator_export',
  ].includes(destination);
}

function buildIcon(destination: Destination) {
  switch (destination) {
    case 'rest_api':
      return ['fak', 'integration'];
    case 'report_exports':
      return ['far', 'file-export'];
    case 'webhook':
      return ['far', 'webhook'];
    case 'pipeline_generation_export':
      return ['fak', 'generate-new-pipeline'];
    case 'deal_navigator_export':
      return ['fak', 'opportunity'];
    default:
      return null;
  }
}

const { addSuccessFlash } = useFlashesStore();
const { talkToSales } = useBilling();

function onAlertClicked() {
  talkToSales('Record Export limit');
  addSuccessFlash({ message: `We'll be in touch shortly` });
}
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";
h3 {
  @apply text-neutral-text-strong font-bold text-base;
}

p {
  @apply text-neutral-text-weak text-sm;
}
</style>
